<template>
  <div class="new-team">
    <organization-breadcrumb :items="[getCurrentClass.name]" />
    <div id="edit-class-page">
      <el-row type="flex" justify="center">
        <el-col :xs="24">
          <h1 class="blue-color">
            {{ $t($i18nLabelsHelper(currentOrganizationType, "edit_group")) }}
          </h1>
          <div>
            <div class="class-edit-main">
              <class-create-details
                ref="classdetail"
                :currentStep="1"
                :componentStep="1"
                v-on:next-step="handleNextStep"
                :isAdmin="true"
                :editMode="true"
                :organizationType="currentOrganizationType"
              />
              <hr class="line-separate" />
              <class-goals
                ref="classgoals"
                :currentStep="2"
                :componentStep="2"
                :editMode="true"
                v-on:next-step="handleNextStep"
              />
              <hr class="line-separate" />
              <assign-license-learners
                ref="assignlicense"
                :currentStep="3"
                :componentStep="3"
                :editMode="true"
                v-on:next-step="handleNextStep"
              />
            </div>
            <div>
              <api-errors :errors="errors" />
              <class-create-footer
                :primaryAction="handleClickSave"
                :primaryPreviousAction="handleCancel"
                :primaryText="$t('common.save')"
                :previousText="$t('common.cancel')"
                v-loading.fullscreen.lock="fullscreenLoading"
              />
            </div>
          </div>
        </el-col>
      </el-row>
    </div>
    <save-edit-class
      :dialogVisible="modals.showConfirmSaveModal"
      v-on:closed="modals.showConfirmSaveModal = false"
      @saveInfo="updateClass"
    />
  </div>
</template>

<script>
// import Steps from '@/components/common/Steps'
import ClassCreateDetails from "@/components/classes/create-classes/ClassCreateDetails";
import ClassGoals from "@/components/classes/create-classes/ClassGoals";
import ClassCreateFooter from "@/components/classes/create-classes/ClassCreateFooter";
import { mapGetters, mapActions } from "vuex";
import { successMessage, warningMessage } from "@/utils/messages";
import OrganizationBreadcrumb from "@/components/common/OrganizationBreadcrumb";
import AssignLicenseLearners from "@/components/classes/edit-classes/AssignLicenseLearners";
import SaveEditClass from "@/components/modals/classes/SaveEditClass.vue";
import ApiErrors from "@/components/common/ApiErrors";

export default {
  components: {
    // Steps,
    ApiErrors,
    ClassCreateDetails,
    ClassGoals,
    ClassCreateFooter,
    AssignLicenseLearners,
    OrganizationBreadcrumb,
    SaveEditClass,
  },
  computed: {
    ...mapGetters({
      getCurrentClass: "classes/getCurrentClass",
      buildingClass: "classes/getBuildingClass",
      currentOrganizationType: "organization/currentOrganizationType",
      currentOrganizationId: "organization/currentOrganizationId",
    }),
  },

  data() {
    return {
      modals: {
        showInviteTeacherModal: false,
        showConfirmSaveModal: false,
      },
      uploadId: null,
      inviteMode: null,

      createClassSteps: {
        steps: [
          {
            title: this.$t("classes.create_class_flow.details"),
          },
          {
            title: this.$t(this.$i18nLabelsHelper(this.currentOrganizationType, "students")),
          },
        ],
      },
      errors: {},
      fullscreenLoading: false
    };
  },
  props: {
    activeComponentProp: {},
    classId: {},
    organizationId: {},
  },
  created() {
    // AssignLicenseLearners use org on mounted
    this.$store.dispatch("organization/setCurrentOrganizationId", {
      organizationId: this.organizationId,
    });
  },
  async mounted() {
    try {
      await this.$store.dispatch("classes/resetBuildingClass");
      await this.$store.dispatch("classes/getClass", {
        organizationId: this.organizationId,
        groupId: this.classId,
      });

      await this.$store.dispatch("classes/getClassGoals", this.classId);

      const currentClass = this.getCurrentClass;

      // set building class base infomation
      if (currentClass) {
        var buildingClassData = {
          className: currentClass.name,
          id: this.classId,
          organization: this.organizationId,
          startDate: currentClass.start_date ? Date.parse(currentClass.start_date) : null,
          endDate: currentClass.end_date ? Date.parse(currentClass.end_date) : null,
        };

        // set building class: teacher infomation
        if (currentClass.owner) {
          buildingClassData = {
            ...buildingClassData,
            teacher: currentClass.owner.email,
            teacher_first_name: currentClass.owner.first_name ? currentClass.owner.first_name : "",
            teacher_last_name: currentClass.owner.last_name ? currentClass.owner.last_name : "",
            teacher_full_name:
              (currentClass.owner.first_name ? currentClass.owner.first_name : "") +
              " " +
              (currentClass.owner.last_name ? currentClass.owner.last_name : ""),
          };
        }
        // set building class goal.
        if (currentClass.goals) {
          buildingClassData = {
            ...buildingClassData,
            daysPractice: currentClass.goals.practice_days_per_week,
            minutesPractice: currentClass.goals.practice_minutes_per_day,
            engagementFirstDate: currentClass.goals.date_assessment_test_first
              ? Date.parse(currentClass.goals.date_assessment_test_first)
              : null,
            engagementSecondDate: currentClass.goals.date_assessment_test_second
              ? Date.parse(currentClass.goals.date_assessment_test_second)
              : null,
          };
        } else if (currentClass.assignment) {
          buildingClassData = {
            ...buildingClassData,
            daysPractice: currentClass.assignment.practice_days_per_week,
            minutesPractice: currentClass.assignment.practice_minutes_per_day,
          };
        }

        // update building class
        await this.updateBuildingClass(buildingClassData);

        // force classGoals and assignlicense update
        this.$refs.classgoals.onUpdateReady();
        this.$refs.assignlicense.fetchPage(1);
      }
    } catch (error) {
      return warningMessage(this.$t("common.generic_error_message"))(this);
    }
  },

  methods: {
    ...mapActions({
      updateBuildingClass: "classes/updateBuildingClass",
      updateClassAction: "classes/updateClass",
      saveLicenseToServer: "classes/saveLicenseToServer",
    }),
    handleNextStep(activeStep, options = {}) {
      // const activeStep = this.createClassSteps.active;
      if (options.mode === "file-import") {
        this.uploadId = options.uploadId;
        this.inviteMode = options.mode;
      }
    },
    handleCancel() {
      // leave
      // this.createClassSteps.active = 1
      this.$store.dispatch("classes/resetBuildingClass");
      return this.$router.push({
        name: "classes",
        params: {
          organizationId: this.currentOrganizationId,
        },
      });
    },
    handleClickSave() {
      this.modals.showConfirmSaveModal = true;
    },
    async updateClass() {
      try {
        this.fullscreenLoading = true;

        //save license
        await this.saveLicenseToServer({ id: this.currentOrganizationId });

        this.$refs.classdetail.handleContinueClick();
        this.$refs.classgoals.setGoals();
        this.$refs.assignlicense.handleClickContinute();

        // if (this.uploadId) {
        //   const response = await this.createClassAction(
        //     dissoc("users", this.classDetails)
        //   );

        //   const groupId = path(["data", "id"], response);
        //   if (groupId) {
        //     await this.patchInviteUploadEmails({
        //       uploadId: this.uploadId,
        //       role: STUDENT,
        //       groupId: groupId
        //     });
        //     await this.postSentInviteUploadEmails({
        //       uploadId: this.uploadId,
        //       consent: this.classDetails.consent
        //     });
        //   }
        // } else {
        await this.updateClassAction(this.buildingClass);
        // }
        successMessage(
          this.$t(
            "classes.success_class_updated"
            // this.$i18nLabelsHelper(
            //   this.currentOrganizationType,
            //   "your_group_was_created"
            // )
          )
        )(this);

        return this.$router.push({
          name: "organizationteam",
          params: {
            organizationId: this.currentOrganizationId,
          },
        });
      } catch (error) {
        this.errors = error;
      } finally {
        this.fullscreenLoading = false;
      }
    },
  },
  destroyed() {
    this.$store.dispatch("classes/resetBuildingClass");
  },
};
</script>

<style lang="scss" scoped>
.new-team {
  width: 100%;
}
#edit-class-page {
  margin: 40px;
  width: 1024px;
  @media only screen and (max-width: 768px) {
    margin: 0 30px;
  }
  h1 {
    border: 0;
  }
  .class-edit-steps {
    margin-top: 25px;
  }
  .class-edit-main {
    background-color: white;
    padding: 40px 0px;
  }
  ::v-deep .base-create-header {
    padding-bottom: 34px;
  }
  ::v-deep .circle-step {
    border: 1px solid #e6e6e6;
    box-sizing: border-box;
    border-radius: 23px;
    width: 40px;
    height: 40px;
    display: inline-flex;
    justify-content: center;
    padding-top: 8px;
    margin-right: 16px;
  }
}

.success-info {
  .success {
    font-weight: bold;
    font-size: 22px;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: #46c779;
    line-height: 30px;
  }
  p {
    margin-top: 20px;
    font-weight: 300;
    font-size: 18px;
    color: #546873;
    margin: 0;
  }
}
::v-deep .create-class-com-disable {
  opacity: 0.5;
}
.line-separate {
  border: 1px solid#E6E6E6;
  margin-bottom: 32px;
}
</style>
